import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { hasPermission } from "../../utils/permissions";
import { apiRequest } from "../../services/api";
import SettlementCorrections from "./SettlementCorrections";
import SettlementAllocations from "./SettlementAllocations";
import SettlementPostingPreview from "./SettlementPostingPreview";
const input =
  "mt-1 min-h-10 w-full rounded-lg border border-slate-200 bg-white p-2 text-sm";
const button =
  "rounded-lg border px-4 py-2 text-sm font-semibold disabled:opacity-40";
const Field = ({ label, name, type = "text", ...props }) => (
  <label className="block text-sm">
    {label}
    <input className={input} name={name} type={type} required {...props} />
  </label>
);
const Select = ({ label, name, children, ...props }) => (
  <label className="block text-sm">
    {label}
    <select className={input} name={name} required {...props}>
      <option value="">Select…</option>
      {children}
    </select>
  </label>
);
export default function SettlementEvidence({ companyId }) {
  const { accessToken, user } = useAuth(),
    canManage = hasPermission(user, "accounting.masters_manage"),
    canWrite = hasPermission(user, "accounting.vouchers_post"),
    canReverse = hasPermission(user, "accounting.vouchers_reverse");
  const [data, setData] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [selected, setSelected] = useState(""),
    [search, setSearch] = useState(""),
    [rows, setRows] = useState([]),
    [payment, setPayment] = useState(""),
    [note, setNote] = useState(""),
    [amount, setAmount] = useState(""),
    [captureScope, setCaptureScope] = useState("SINGLE_TRANSACTION"),
    [preview, setPreview] = useState(null),
    [confirmed, setConfirmed] = useState(false);
  const gen = useRef(0),
    keys = useRef({});
  useEffect(
    () => () => {
      gen.current++;
    },
    [],
  );
  const url = `/accounting/${companyId}/settlement-evidence`;
  function requestKey(kind, body) {
    const signature = JSON.stringify(body);
    if (keys.current[kind]?.signature !== signature) {
      if (!window.crypto?.randomUUID)
        throw Error("HTTPS or localhost is required for secure request keys.");
      keys.current[kind] = { signature, key: window.crypto.randomUUID() };
    }
    return keys.current[kind].key;
  }
  async function run(work, done) {
    const g = ++gen.current;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const r = await work();
      if (g === gen.current) done(r);
    } catch (e) {
      if (g === gen.current) setError(e.message);
    } finally {
      if (g === gen.current) setBusy(false);
    }
  }
  function load() {
    setSelected("");
    setPreview(null);
    setConfirmed(false);
    setRows([]);
    run(() => apiRequest(url, { accessToken }), setData);
  }
  function save(e, kind, path, transform = (x) => x) {
    e.preventDefault();
    const form = e.currentTarget;
    const raw = Object.fromEntries(new FormData(form));
    run(
      async () => {
        const body = transform(raw);
        await apiRequest(url + path, {
          accessToken,
          method: "POST",
          body: { ...body, requestKey: requestKey(kind, body) },
        });
        return apiRequest(url, { accessToken });
      },
      (r) => {
        setData(r);
        setNotice(
          "Saved immutable evidence history. No financial entry or refund was posted.",
        );
        form.reset();
      },
    );
  }
  function change(setter, value) {
    gen.current++;
    setter(value);
    setPreview(null);
    setConfirmed(false);
    setError("");
    setNotice("");
  }
  function choose(id) {
    setAmount("");
    change(setSelected, id);
    setSearch("");
    setRows([]);
    setPayment("");
    setNote("");
  }
  function find(e) {
    e.preventDefault();
    run(
      () =>
        apiRequest(
          `${url}/${selected}/candidates?search=${encodeURIComponent(search.trim())}`,
          { accessToken },
        ),
      (r) => {
        setRows(r.rows);
        setPayment("");
        setPreview(null);
        setConfirmed(false);
      },
    );
  }
  function review(e) {
    e.preventDefault();
    run(
      () =>
        apiRequest(`${url}/${selected}/preview`, {
          accessToken,
          method: "POST",
          body: { paymentId: payment, note, ...(amount ? { amount } : {}) },
        }),
      (r) => {
        setPreview(r);
        setConfirmed(false);
      },
    );
  }
  function match() {
    if (!confirmed || busy || !preview) return;
    const body = {
      paymentId: payment,
      note,
      previewHash: preview.previewHash,
      evidenceReviewed: true,
      ...(preview.payload.allocation
        ? { amount: preview.payload.allocation.gross }
        : {}),
    };
    run(
      async () => {
        await apiRequest(`${url}/${selected}/match`, {
          accessToken,
          method: "POST",
          body: { ...body, requestKey: requestKey("match", body) },
        });
        return apiRequest(url, { accessToken });
      },
      (r) => {
        setData(r);
        setPreview(null);
        setConfirmed(false);
        setRows([]);
        setSelected("");
        setNotice(
          "Match request resolved; current and released history is shown below. Bank/provider verification and financial posting remain separate.",
        );
      },
    );
  }
  return (
    <section className="space-y-5 rounded-2xl border bg-white p-5">
      <header>
        <h2 className="text-xl font-semibold">
          Settlement evidence & reviewed matching
        </h2>
        <p className="mt-2 text-sm text-slate-600">
          Manual source references only—not an uploaded statement or provider
          verification. Bank credits may cover batch or partial payments within
          one company/GST/store/channel/method. Chargebacks and refunds remain
          separate; unmatched balances stay visible.
        </p>
      </header>
      <button className={button} disabled={!companyId || busy} onClick={load}>
        Load evidence workspace
      </button>
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="text-emerald-800">
          {notice}
        </p>
      )}
      {data && (
        <>
          <p className="rounded-lg bg-amber-50 p-3 text-sm">
            No settlement vouchers, bank payments or refunds are created here. A
            manual match does not mean Cashfree or the bank independently
            verified the credit. History below shows the latest{" "}
            {data.limits?.evidence || 100} evidence records.
          </p>
          {canManage && (
            <details>
              <summary className="cursor-pointer font-semibold">
                1. Configure an explicit collection channel
              </summary>
              <form
                className="mt-4"
                onSubmit={(e) => save(e, "channel", "/channels")}
              >
                <fieldset disabled={busy} className="grid gap-3 md:grid-cols-3">
                  <Select
                    label="Channel GST registration"
                    name="registrationId"
                  >
                    {data.registrations.map((r) => (
                      <option value={r.id} key={r.id}>
                        {r.name} · {r.gstin}
                      </option>
                    ))}
                  </Select>
                  <Select label="Channel store" name="storeId">
                    {data.stores.map((s) => (
                      <option value={s.id} key={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </Select>
                  <Select label="Receiving bank ledger" name="bankLedgerId">
                    {data.banks.map((b) => (
                      <option value={b.id} key={b.id}>
                        {b.code} · {b.name}
                      </option>
                    ))}
                  </Select>
                  <Select label="Collection channel type" name="kind">
                    <option value="CASHFREE">Cashfree</option>
                    <option value="TERMINAL">Separate terminal</option>
                    <option value="BANK_UPI">Direct-bank UPI</option>
                  </Select>
                  <Select label="Channel payment method" name="method">
                    <option value="CARD">Card</option>
                    <option value="UPI">UPI</option>
                  </Select>
                  <Field
                    label="Channel name"
                    name="name"
                    minLength={2}
                    maxLength={120}
                  />
                  <Field
                    label="Merchant / terminal / account label (no secrets)"
                    name="providerAccount"
                    minLength={2}
                    maxLength={120}
                  />
                  <button className={button}>Save immutable channel</button>
                </fieldset>
              </form>
            </details>
          )}
          {canWrite && (
            <details>
              <summary className="cursor-pointer font-semibold">
                2. Capture bank-credit evidence
              </summary>
              <form
                className="mt-4"
                onSubmit={(e) =>
                  save(e, "capture", "", (raw) => {
                    const { captureEvidenceConfirmed, ...rest } = raw;
                    return {
                      ...rest,
                      ...(raw.evidenceScope === "BATCH_CREDIT"
                        ? {
                            batchCreditConfirmed:
                              captureEvidenceConfirmed === "on",
                          }
                        : {
                            singleTransactionCreditConfirmed:
                              captureEvidenceConfirmed === "on",
                          }),
                    };
                  })
                }
              >
                <fieldset disabled={busy} className="grid gap-3 md:grid-cols-3">
                  <Select
                    label="Evidence credit scope"
                    name="evidenceScope"
                    value={captureScope}
                    onChange={(e) => setCaptureScope(e.target.value)}
                  >
                    <option value="SINGLE_TRANSACTION">
                      Single transaction / partial credit
                    </option>
                    <option value="BATCH_CREDIT">Batch bank credit</option>
                  </Select>
                  <Select label="Evidence collection channel" name="channelId">
                    {data.channels
                      .filter((c) => !c.retiredId)
                      .map((c) => (
                        <option value={c.id} key={c.id}>
                          {c.name} · {c.kind} · {c.method} · {c.providerAccount}
                        </option>
                      ))}
                  </Select>
                  <Field
                    label="External transaction ID"
                    name="externalTransactionId"
                    maxLength={150}
                  />
                  <Field
                    label="Provider settlement reference"
                    name="settlementReference"
                    maxLength={150}
                  />
                  <Field
                    label="Bank credit reference"
                    name="bankCreditReference"
                    maxLength={150}
                  />
                  <Field
                    label="Transaction date (India)"
                    name="transactionDate"
                    type="date"
                  />
                  <Field
                    label="Bank credit date (India)"
                    name="bankDate"
                    type="date"
                  />
                  <Field
                    label="Transactions through date (India)"
                    name="transactionDateThrough"
                    type="date"
                    required={false}
                  />
                  {["gross", "fees", "feeTax", "net"].map((n) => (
                    <Field
                      key={n}
                      label={
                        n === "feeTax"
                          ? "Fee tax ₹"
                          : `${n[0].toUpperCase() + n.slice(1)} ₹`
                      }
                      name={n}
                      inputMode="decimal"
                      pattern="[0-9]{1,12}(\.[0-9]{1,2})?"
                      defaultValue={
                        ["fees", "feeTax"].includes(n) ? "0.00" : undefined
                      }
                    />
                  ))}
                  <Field
                    label="Provider document / row reference"
                    name="providerEvidence"
                    minLength={5}
                    maxLength={500}
                  />
                  <Field
                    label="Bank statement / row reference"
                    name="bankEvidence"
                    minLength={5}
                    maxLength={500}
                  />
                  <Field
                    label="Evidence capture note"
                    name="note"
                    minLength={20}
                    maxLength={1000}
                  />
                  <label className="md:col-span-3">
                    <input
                      name="captureEvidenceConfirmed"
                      type="checkbox"
                      required
                    />{" "}
                    I checked the single/batch scope, transaction date range,
                    provider occurrence and bank credit against both source
                    documents; this is not a refund or chargeback.
                  </label>
                  <button className={button}>
                    Capture evidence — no posting
                  </button>
                </fieldset>
              </form>
            </details>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <caption className="text-left font-semibold">
                Evidence history — provider verification not performed
              </caption>
              <thead>
                <tr>
                  {[
                    "Channel / transaction",
                    "Bank credit",
                    "Gross / fees / fee tax / net ₹",
                    "Evidence references",
                    "Review",
                  ].map((h) => (
                    <th className="p-2" key={h}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.evidence.map((e) => (
                  <tr className="border-t" key={e.id}>
                    <td className="p-2">
                      {data.channels.find((c) => c.id === e.channelId)?.name ||
                        e.channelId}
                      <span className="block">{e.externalTransactionId}</span>
                    </td>
                    <td>
                      {e.bankCreditReference}
                      <span className="block">{e.bankDate.slice(0, 10)}</span>
                    </td>
                    <td>
                      {e.gross} / {e.fees} / {e.feeTax} / {e.net}
                      <small className="block">
                        Allocated gross ₹
                        {e.allocatedGross ?? (e.match ? e.gross : "0.00")} ·
                        remaining gross ₹
                        {e.remainingGross ?? (e.match ? "0.00" : e.gross)} ·
                        remaining net ₹{e.remainingNet ?? e.net}
                      </small>
                    </td>
                    <td>
                      <span className="block">{e.providerEvidence}</span>
                      <span className="block">{e.bankEvidence}</span>
                    </td>
                    <td>
                      {e.supersededId ? (
                        <span>Superseded — retained in history</span>
                      ) : e.match ? (
                        <>
                          <span className="block">
                            {e.posting?.reversal ? `Reversed by ${e.posting.reversal.voucher?.number || e.posting.reversal.voucherId}` : e.posting ? `Posted ${e.posting.voucher?.number || e.posting.voucherId} · allocations frozen` : 'Manually reviewed · no posting'}
                          </span>
                          <span className="block">
                            {e.match.snapshot.receiptNumber}
                          </span>
                        </>
                      ) : (
                        <button
                          className={button}
                          disabled={
                            busy ||
                            e.remainingGross === "0.00" ||
                            !!data.channels.find((c) => c.id === e.channelId)
                              ?.retiredId
                          }
                          onClick={() => choose(e.id)}
                        >
                          Review payment match
                        </button>
                      )}
                      {!e.supersededId &&
                        e.match &&
                        e.remainingGross &&
                        e.remainingGross !== "0.00" && (
                          <button
                            className={button}
                            disabled={busy}
                            onClick={() => choose(e.id)}
                          >
                            Review another partial allocation
                          </button>
                        )}
                    </td>
                  </tr>
                ))}
                {!data.evidence.length && (
                  <tr>
                    <td colSpan={5}>No settlement evidence captured.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <SettlementPostingPreview data={data} companyId={companyId} accessToken={accessToken} run={run} busy={busy} canManage={canManage} canWrite={canWrite} canReverse={canReverse} requestKey={requestKey} onPosted={setData} />
          <SettlementAllocations
            data={data}
            companyId={companyId}
            accessToken={accessToken}
            canWrite={canWrite}
            busy={busy}
            run={run}
            requestKey={requestKey}
            onBegin={() => {
              setSelected("");
              setPreview(null);
              setConfirmed(false);
            }}
            onSaved={(r) => {
              setData(r);
              setSelected("");
              setPreview(null);
              setConfirmed(false);
              setNotice(
                "Allocation request resolved. Current remaining balances and release history are shown; no financial posting.",
              );
            }}
          />
          <SettlementCorrections
            data={data}
            companyId={companyId}
            accessToken={accessToken}
            canManage={canManage}
            canWrite={canWrite}
            busy={busy}
            run={run}
            requestKey={requestKey}
            onSaved={(r) => {
              setData(r);
              setSelected("");
              setPreview(null);
              setConfirmed(false);
              setRows([]);
              setNotice(
                "Correction request resolved. History is retained; no financial posting or refund was made.",
              );
            }}
            onBegin={() => {
              setSelected("");
              setPreview(null);
              setConfirmed(false);
              setRows([]);
            }}
          />
          {selected && (
            <section className="space-y-3 rounded-xl border p-4">
              <h3 className="font-semibold">
                3. Compare original payment with selected evidence
              </h3>
              <form onSubmit={find} className="flex items-end gap-3">
                <Field
                  label="Original POS receipt search"
                  value={search}
                  onChange={(e) => {
                    change(setSearch, e.target.value);
                    setRows([]);
                    setPayment("");
                  }}
                  minLength={2}
                  maxLength={80}
                  disabled={busy}
                />
                <button className={button} disabled={busy}>
                  Find original payments
                </button>
              </form>
              <form onSubmit={review}>
                <fieldset disabled={busy} className="grid gap-3">
                  <Select
                    label="Original payment to compare"
                    value={payment}
                    onChange={(e) => change(setPayment, e.target.value)}
                  >
                    {rows.map((p) => (
                      <option
                        value={p.paymentId}
                        key={p.paymentId}
                        disabled={p.paymentRemaining === "0.00"}
                      >
                        {p.receiptNumber} · {p.method} · ₹{p.amount} ·{" "}
                        {p.reference || "No recorded reference"}
                        {" · Remaining ₹"}
                        {p.paymentRemaining ?? p.amount}
                      </option>
                    ))}
                  </Select>
                  <Field
                    label="Evidence comparison note"
                    value={note}
                    onChange={(e) => change(setNote, e.target.value)}
                    minLength={20}
                    maxLength={1000}
                  />
                  <Field
                    label="Partial gross amount to allocate ₹ (blank means full evidence gross)"
                    required={false}
                    value={amount}
                    onChange={(e) => change(setAmount, e.target.value)}
                    inputMode="decimal"
                  />
                  <button className={button} disabled={!payment || busy}>
                    Preview evidence match — no changes
                  </button>
                </fieldset>
              </form>
              {preview && (
                <div className="space-y-3 rounded-lg bg-slate-50 p-4">
                  <p>
                    Original {preview.payload.receiptNumber} ·{" "}
                    {preview.payload.method} · Allocation gross ₹
                    {preview.payload.allocation?.gross ?? preview.payload.gross}
                    , fees ₹
                    {preview.payload.allocation?.fees ?? preview.payload.fees},
                    fee tax ₹
                    {preview.payload.allocation?.feeTax ??
                      preview.payload.feeTax}
                    , net share ₹
                    {preview.payload.allocation?.net ?? preview.payload.net}.
                  </p>
                  <p className="text-sm">{preview.notice}</p>
                  {preview.payload.remainingAfter && (
                    <p>
                      Evidence remaining gross ₹
                      {preview.payload.remainingAfter.gross} · payment remaining
                      gross ₹{preview.payload.paymentRemainingAfter}
                    </p>
                  )}
                  {canWrite && (
                    <>
                      <label className="block">
                        <input
                          type="checkbox"
                          checked={confirmed}
                          disabled={busy}
                          onChange={(e) => setConfirmed(e.target.checked)}
                        />{" "}
                        I compared the original full payment with both retained
                        provider and bank documents, including dates,
                        references, fees and net credit.
                      </label>
                      <button
                        className={button}
                        disabled={busy || !confirmed}
                        onClick={match}
                      >
                        Record reviewed evidence match — no posting
                      </button>
                    </>
                  )}
                </div>
              )}
            </section>
          )}
        </>
      )}
    </section>
  );
}
